<template>
  <div class="pdf-upload">
    <div 
      class="upload-area"
      :class="{ 'drag-over': dragOver, 'has-file': file }"
      @dragover.prevent="handleDragOver"
      @dragleave.prevent="handleDragLeave"
      @drop.prevent="handleDrop"
      @click="triggerFileInput"
    >
      <div class="upload-icon">
        📄
      </div>
      
      <h3 v-if="!file">{{ $t('editor.uploadYourResumePdf') }}</h3>
      <p v-if="!file" class="upload-instructions">
        {{ $t('editor.dragDropOrClick') }}
      </p>
      
      <div v-if="file" class="file-info">
        <div class="file-icon">📄</div>
        <div class="file-details">
          <h4>{{ file.name }}</h4>
          <p>{{ formatFileSize(file.size) }}</p>
        </div>
        <button @click.stop="removeFile" class="remove-btn" :title="$t('editor.removeFile')">
          ×
        </button>
      </div>
      
      <input
        ref="fileInput"
        type="file"
        accept=".pdf,application/pdf"
        @change="handleFileSelect"
        style="display: none"
      />
    </div>
    
    <div v-if="file" class="upload-actions">
      <button 
        @click="uploadFile" 
        class="btn btn-primary"
        :disabled="uploading"
      >
        <span v-if="uploading" class="spinner"></span>
        <span v-else>{{ $t('editor.processResume') }}</span>
      </button>
      
      <button 
        @click="removeFile" 
        class="btn btn-secondary"
        :disabled="uploading"
      >
        {{ $t('editor.chooseDifferentFile') }}
      </button>
    </div>
    
    <div v-if="error" class="error-message">
      ⚠️ {{ error }}
    </div>
    
    <div class="upload-tips">
      <h4>{{ $t('editor.tipsForBestResults') }}</h4>
      <ul>
        <li>{{ $t('editor.tipCleanPdf') }}</li>
        <li>{{ $t('editor.tipSelectable') }}</li>
        <li>{{ $t('editor.tipMaxSize') }}</li>
        <li>{{ $t('editor.tipOcr') }}</li>
      </ul>
    </div>
  </div>
</template>

<script>
import { ref } from 'vue'
import { toast } from 'vue3-toastify'
import api from '../services/api'

export default {
  name: 'PdfUpload',
  emits: ['upload-success'],
  setup(props, { emit }) {
    const fileInput = ref(null)
    const file = ref(null)
    const dragOver = ref(false)
    const uploading = ref(false)
    const error = ref('')

    const triggerFileInput = () => {
      if (!file.value) {
        fileInput.value.click()
      }
    }

    const handleFileSelect = (event) => {
      const selectedFile = event.target.files[0]
      if (selectedFile) {
        validateAndSetFile(selectedFile)
      }
    }

    const handleDragOver = () => {
      dragOver.value = true
    }

    const handleDragLeave = () => {
      dragOver.value = false
    }

    const handleDrop = (event) => {
      dragOver.value = false
      const droppedFile = event.dataTransfer.files[0]
      if (droppedFile) {
        validateAndSetFile(droppedFile)
      }
    }

    const validateAndSetFile = (selectedFile) => {
      error.value = ''
      
      // DEBUG: Log file information
      console.log('=== PdfUpload.vue: File Validation Debug ===')
      console.log('File name:', selectedFile.name)
      console.log('File type:', selectedFile.type)
      console.log('File size (bytes):', selectedFile.size)
      console.log('File size (KB):', (selectedFile.size / 1024).toFixed(2))
      console.log('File size (MB):', (selectedFile.size / (1024 * 1024)).toFixed(2))
      console.log('File size type:', typeof selectedFile.size)
      console.log('File size is NaN:', isNaN(selectedFile.size))
      console.log('File size is finite:', isFinite(selectedFile.size))
      
      // Check file type
      if (selectedFile.type !== 'application/pdf' && !selectedFile.name.toLowerCase().endsWith('.pdf')) {
        console.warn('File type validation FAILED:', selectedFile.type)
        error.value = 'Please select a PDF file'
        return
      }
      console.log('File type validation PASSED')
      
      // Check file size (10MB limit)
      const maxSize = 10 * 1024 * 1024 // 10MB
      console.log('Max size (bytes):', maxSize)
      console.log('Max size (KB):', (maxSize / 1024).toFixed(2))
      console.log('Max size (MB):', (maxSize / (1024 * 1024)).toFixed(2))
      console.log('Max size type:', typeof maxSize)
      console.log('Comparison: selectedFile.size > maxSize')
      console.log('  Left side (file size):', selectedFile.size, typeof selectedFile.size)
      console.log('  Right side (max size):', maxSize, typeof maxSize)
      console.log('  Result:', selectedFile.size > maxSize)
      console.log('  Difference (bytes):', selectedFile.size - maxSize)
      console.log('  Difference (MB):', ((selectedFile.size - maxSize) / (1024 * 1024)).toFixed(2))
      
      if (selectedFile.size > maxSize) {
        console.error('❌ File size validation FAILED')
        console.error('  File size exceeds limit by:', ((selectedFile.size - maxSize) / (1024 * 1024)).toFixed(2), 'MB')
        error.value = 'File size exceeds 10MB limit'
        return
      }
      
      console.log('✅ File size validation PASSED')
      console.log('=== End File Validation Debug ===')
      file.value = selectedFile
    }

    const removeFile = () => {
      file.value = null
      error.value = ''
      if (fileInput.value) {
        fileInput.value.value = ''
      }
    }

    const formatFileSize = (bytes) => {
      if (bytes === 0) return '0 Bytes'
      const k = 1024
      const sizes = ['Bytes', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    }

    const uploadFile = async () => {
      if (!file.value) {
        error.value = 'Please select a file first'
        return
      }

      uploading.value = true
      error.value = ''

      try {
        console.log('=== PdfUpload.vue: Starting file upload ===')
        console.log('File name:', file.value.name)
        console.log('File size (bytes):', file.value.size)
        console.log('File size (MB):', (file.value.size / (1024 * 1024)).toFixed(2))
        const response = await api.uploadResume(file.value)
        console.log('✅ Upload successful:', response)
        emit('upload-success', response)
        toast.success('Resume processed successfully!')
      } catch (err) {
        console.error('❌ Upload failed with error:')
        console.error('Error object:', err)
        console.error('Error message:', err.message)
        console.error('Error response:', err.response)
        console.error('Error response data:', err.response?.data)
        console.error('Error response status:', err.response?.status)
        console.error('Error response statusText:', err.response?.statusText)
        console.error('Full error:', JSON.stringify(err, null, 2))
        error.value = err.message || 'Failed to upload resume. Please try again.'
        toast.error('Upload failed: ' + err.message)
      } finally {
        uploading.value = false
      }
    }

    return {
      fileInput,
      file,
      dragOver,
      uploading,
      error,
      triggerFileInput,
      handleFileSelect,
      handleDragOver,
      handleDragLeave,
      handleDrop,
      removeFile,
      formatFileSize,
      uploadFile
    }
  }
}
</script>

<style scoped>
.pdf-upload {
  max-width: 600px;
  margin: 0 auto;
}

.upload-area {
  border: 3px dashed var(--color-border);
  border-radius: var(--radius-lg);
  padding: 3rem 2rem;
  text-align: center;
  cursor: pointer;
  transition: border-color var(--transition-slow), background-color var(--transition-slow), transform var(--transition-slow);
  background-color: var(--color-surface-elevated);
  margin-bottom: 2rem;
}

.upload-area:hover {
  border-color: var(--color-primary);
  background-color: var(--color-surface-muted);
}

.upload-area.drag-over {
  border-color: var(--color-primary);
  background-color: #e0f2fe;
  transform: scale(1.02);
}

.upload-area.has-file {
  border-style: solid;
  border-color: var(--color-success);
  background-color: #d1fae5;
}

.upload-icon {
  font-size: 4rem;
  margin-bottom: 1rem;
  opacity: 0.7;
}

.upload-area.has-file .upload-icon {
  display: none;
}

.upload-area h3 {
  color: var(--color-text);
  margin-bottom: 0.5rem;
  font-size: 1.5rem;
}

.upload-instructions {
  color: var(--color-text-muted);
  font-size: 1rem;
}

.file-info {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 1rem;
  background: var(--color-surface-elevated);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-card);
}

.file-icon {
  font-size: 2.5rem;
}

.file-details {
  text-align: left;
  flex: 1;
}

.file-details h4 {
  margin: 0;
  color: var(--color-text);
  font-size: 1.1rem;
  word-break: break-all;
}

.file-details p {
  margin: 0.25rem 0 0 0;
  color: var(--color-text-muted);
  font-size: 0.9rem;
}

.remove-btn {
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #e53e3e;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 50%;
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.remove-btn:hover {
  background-color: #fed7d7;
}

.upload-actions {
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin-bottom: 2rem;
}

.btn {
  padding: 0.75rem 2rem;
  border: none;
  border-radius: var(--radius-sm);
  font-weight: 600;
  cursor: pointer;
  transition: transform var(--transition-normal), box-shadow var(--transition-normal), background-color var(--transition-normal);
  min-width: 180px;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-primary {
  background: var(--gradient-hero);
  color: var(--color-primary-on);
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 12px rgba(2, 132, 199, 0.35);
}

.btn-secondary {
  background: var(--color-border);
  color: var(--color-text-muted);
}

.btn-secondary:hover:not(:disabled) {
  background: #cbd5e0;
}

.spinner {
  display: inline-block;
  width: 1rem;
  height: 1rem;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: white;
  animation: spin 1s ease-in-out infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.error-message {
  background-color: #fed7d7;
  color: #9b2c2c;
  padding: 1rem;
  border-radius: 6px;
  margin-bottom: 1.5rem;
  text-align: center;
  border: 1px solid #fc8181;
}

.upload-tips {
  background-color: var(--color-surface-muted);
  border-radius: var(--radius-md);
  padding: 1.5rem;
  border: 1px solid var(--color-border);
}

.upload-tips h4 {
  color: var(--color-text);
  margin-bottom: 0.75rem;
  font-size: 1.1rem;
}

.upload-tips ul {
  list-style-type: none;
  padding: 0;
  margin: 0;
}

.upload-tips li {
  color: var(--color-text-muted);
  padding: 0.25rem 0;
  padding-left: 1.5rem;
  position: relative;
}

.upload-tips li:before {
  content: "•";
  color: var(--color-primary);
  position: absolute;
  left: 0.5rem;
}
</style>
